import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { 
  ArrowRight, CheckCircle, Clock, TrendingUp, TrendingDown,
  Shield, AlertTriangle, Target, Bell, Smartphone, MessageCircle,
  DollarSign, BarChart3, Brain, Zap, Lock, Eye, Users, Star,
  ChevronRight, ArrowDown, Sparkles, HelpCircle, Mail, Cpu,
  Activity, LineChart, Settings, Filter, Gauge
} from 'lucide-react';

// SEO-optimized FAQ data - Questions match real search queries
const faqData = [
  {
    question: "AI kripto sinyalleri nasıl üretilir?",
    answer: "Borsamex'in Sniper Engine sistemi, 200'den fazla teknik göstergeyi analiz ederek potansiyel fırsatları tespit eder. Sistem deterministik kurallarla çalışır - AI sadece piyasa durumunu yorumlar, kararları algoritma verir."
  },
  {
    question: "Kripto sinyallerinde TP ve Stop seviyeleri nasıl belirlenir?",
    answer: "TP (Take Profit) ve SL (Stop Loss) seviyeleri, ATR (Average True Range) bazlı volatilite analizi ile hesaplanır. Her coin için piyasa koşullarına göre dinamik olarak belirlenir."
  },
  {
    question: "Kripto sinyalleri her zaman doğru çıkar mı?",
    answer: "Hayır, hiçbir sistem %100 doğruluk garantisi veremez. Geçmiş performans gelecek sonuçları garanti etmez. Borsamex şeffaf performans verileri sunar."
  },
  {
    question: "Borsamex'te AI mi yoksa algoritma mı karar veriyor?",
    answer: "Kararları Sniper Engine (deterministik algoritma) verir. AI (GPT-4o) sadece piyasa durumunu yorumlar ve açıklama üretir."
  },
  {
    question: "Borsamex borsama bağlanır mı?",
    answer: "Hayır! Borsamex sadece sinyal ve analiz sağlar. Borsanıza bağlanmıyoruz, API key istemiyoruz, fonlarınıza erişimimiz yok."
  },
  {
    question: "Kripto sinyal üretim süreci nasıl işliyor?",
    answer: "Sistem 7/24 piyasayı tarar, teknik göstergeler hesaplanır, Sniper Engine skorlama yapar, minimum skoru geçen setuplar sinyal olarak yayınlanır."
  },
  {
    question: "Telegram kripto sinyalleri ile web arasındaki fark nedir?",
    answer: "Web sitesinde tüm sinyal detaylarını görebilirsiniz. Telegram bildirimleri ise anlık uyarılar ve günlük raporlar içerir. Pro üyelik ile Telegram erişimi açılır."
  },
  {
    question: "Borsamex ücretsiz mi kullanılabilir?",
    answer: "Ücretsiz plan süresizdir. Temel sinyalleri web sitesinde her zaman görebilirsiniz. Pro üyelik ile Telegram bildirimleri açılır."
  }
];

// JSON-LD Structured Data for SEO
const structuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.answer
    }
  }))
};

// Organization Schema - Complete with logo and social links for E-E-A-T signals
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Borsamex",
  "url": "https://borsamex.com",
  "logo": "https://borsamex.com/logo512.png",
  "image": "https://borsamex.com/logo512.png",
  "description": "Yapay zeka destekli kripto trading sinyal platformu. Sniper Engine teknolojisi ile 7/24 piyasa analizi.",
  "sameAs": [
    "https://t.me/BorsamexTR",
    "https://t.me/BorsamexEN"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer support",
    "availableLanguage": ["Turkish", "English"]
  }
};

const HowItWorksPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      {/* Meta Title: 58 chars | Description: 155 chars - Optimized for CTR */}
      <SEO 
        title="AI Kripto Sinyalleri Nasıl Çalışır? – Sniper Engine | Borsamex"
        description="Borsamex'in AI destekli Sniper Engine'i kripto piyasalarını 7/24 tarar, teknik seviyeleri hesaplar ve risk odaklı sinyaller üretir. Nasıl çalıştığını görün."
        keywords="ai kripto sinyalleri nasıl çalışır, yapay zeka trading sinyalleri, kripto sinyal üretim süreci, algoritmik sinyal motoru, teknik analiz sistemi, otomatik piyasa tarama"
        canonical="https://borsamex.com/nasil-calisir"
        structuredData={[structuredData, organizationSchema]}
      />

      {/* HERO */}
      <section className="py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-green/5 via-transparent to-blue-500/5" />
        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <Badge className="mb-6 px-4 py-2 bg-slate-800 text-slate-300 border-slate-700">
              <Cpu className="h-4 w-4 mr-2" />
              Sniper Engine v8.0 Teknolojisi
            </Badge>
            
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight">
              AI Destekli Kripto Sinyalleri<br />
              <span className="text-brand-green">Nasıl Çalışır?</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Borsamex'in <strong className="text-white">algoritmik sinyal motoru</strong>, yapay zeka ve teknik analiz sistemini birleştirerek 
              kripto piyasasında potansiyel fırsatları tespit eder. Otomatik piyasa tarama <strong className="text-brand-green">7/24</strong> çalışır.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                  Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/performance">
                <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  <BarChart3 className="mr-2 h-5 w-5" />
                  Performans Verileri
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI ve Teknik Analiz */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              AI ve Teknik Analiz Sinyalleri Nedir?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Kripto sinyalleri, teknik analiz ve yapay zeka algoritmalarının birlikte çalışmasıyla üretilen fırsat bildirimleridir.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <Target className="h-5 w-5 text-brand-green" />
                  Sniper Engine Teknolojisi
                </h3>
                <p className="text-slate-400">
                  Sniper Engine, 200'den fazla teknik göstergeyi (RSI, MACD, Bollinger Bands, ATR vb.) analiz ederek potansiyel giriş noktalarını tespit eder.
                  <strong className="text-white"> Deterministik kurallarla</strong> çalıştığı için tutarlı sonuçlar üretir.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <Brain className="h-5 w-5 text-purple-400" />
                  AI'ın Rolü: Yorum, Karar Değil
                </h3>
                <p className="text-slate-400">
                  GPT-4o tabanlı AI sistemi <strong className="text-white">karar vermez</strong> - sadece piyasa durumunu yorumlar. 
                  Kararları Sniper Engine verir, AI açıklama ve bağlam sağlar.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Card className="border-slate-700/50 bg-slate-900/80">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {[
                      { icon: <Activity className="h-5 w-5" />, label: "Piyasa Verisi", desc: "7/24 canlı veri akışı", color: "text-blue-400" },
                      { icon: <Filter className="h-5 w-5" />, label: "Teknik Filtre", desc: "200+ gösterge analizi", color: "text-amber-400" },
                      { icon: <Gauge className="h-5 w-5" />, label: "Sniper Skorlama", desc: "Kalite puanı hesaplama", color: "text-purple-400" },
                      { icon: <Brain className="h-5 w-5" />, label: "AI Yorumu", desc: "Piyasa bağlamı açıklaması", color: "text-cyan-400" },
                      { icon: <Bell className="h-5 w-5" />, label: "Sinyal Yayını", desc: "Web + Telegram bildirimi", color: "text-brand-green" }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4 p-3 bg-slate-800/50 rounded-lg">
                        <div className={`w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center ${item.color}`}>
                          {item.icon}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-white text-sm">{item.label}</p>
                          <p className="text-xs text-slate-500">{item.desc}</p>
                        </div>
                        {idx < 4 && <ArrowDown className="h-4 w-4 text-slate-600" />}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Adım Adım Süreç */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 px-3 py-1 bg-blue-500/10 text-blue-400 border-blue-500/20">
              <Settings className="h-3 w-3 mr-1" /> Süreç
            </Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Adım Adım Sinyal Süreci</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "1", icon: <Eye className="h-7 w-7" />, title: "7/24 Piyasa Tarama", desc: "Sniper Engine seçili kripto pariteleri sürekli izler.", color: "bg-blue-500/20 text-blue-400" },
              { step: "2", icon: <LineChart className="h-7 w-7" />, title: "Teknik Hesaplama", desc: "RSI, MACD, Bollinger, ATR göstergeleri hesaplanır.", color: "bg-amber-500/20 text-amber-400" },
              { step: "3", icon: <Bell className="h-7 w-7" />, title: "Bildirim", desc: "Kalite skorunu geçen sinyaller anında yayınlanır.", color: "bg-purple-500/20 text-purple-400" },
              { step: "4", icon: <Target className="h-7 w-7" />, title: "Pozisyon Takibi", desc: "Aktif sinyaller sürekli izlenir, güncellemeler gelir.", color: "bg-brand-green/20 text-brand-green" }
            ].map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }} viewport={{ once: true }}>
                <Card className="border-slate-700/50 bg-slate-900/50 h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center`}>{item.icon}</div>
                      <span className="text-2xl font-bold text-slate-600">#{item.step}</span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3">{item.title}</h3>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TP/SL Hesaplama */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">TP / Stop Seviyeleri Nasıl Hesaplanır?</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Card className="border-green-500/50 bg-gradient-to-br from-green-900/20 to-slate-900/80 max-w-md mx-auto">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-green-400" />
                    </div>
                    <div>
                      <span className="font-bold text-white text-xl">BTC-USDT</span>
                      <Badge className="ml-2 bg-green-500/20 text-green-400 border-green-500/30 text-xs">LONG</Badge>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-slate-800/50 rounded-xl mb-4">
                    <p className="text-xs text-slate-500 mb-1">GİRİŞ FİYATI</p>
                    <p className="font-mono text-2xl text-white font-bold">$97,500</p>
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between p-3 bg-green-900/20 rounded-lg border border-green-500/20">
                      <div><p className="text-xs text-green-400 font-medium">TP1</p><p className="text-xs text-slate-500">1.5x ATR</p></div>
                      <span className="font-mono text-green-400 font-bold">$99,000</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-emerald-900/20 rounded-lg border border-emerald-500/20">
                      <div><p className="text-xs text-emerald-400 font-medium">TP2</p><p className="text-xs text-slate-500">2.5x ATR</p></div>
                      <span className="font-mono text-emerald-400 font-bold">$101,500</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-cyan-900/20 rounded-lg border border-cyan-500/20">
                      <div><p className="text-xs text-cyan-400 font-medium">TP3</p><p className="text-xs text-slate-500">4x ATR</p></div>
                      <span className="font-mono text-cyan-400 font-bold">$105,000</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-red-900/20 rounded-lg border border-red-500/20">
                    <div><p className="text-xs text-red-400 font-medium">STOP LOSS</p><p className="text-xs text-slate-500">1x ATR</p></div>
                    <span className="font-mono text-red-400 font-bold">$95,800</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-3">ATR Bazlı Hesaplama</h3>
                <p className="text-slate-400 text-sm">ATR (Average True Range), volatiliteyi ölçer. Borsamex her coin için ATR değerini hesaplayarak TP ve SL seviyelerini dinamik olarak belirler.</p>
              </div>

              <div className="space-y-3">
                {[
                  { label: "TP1", desc: "İlk Kar Al (1.5x ATR) - %30 pozisyon", color: "bg-green-500/20 text-green-400" },
                  { label: "TP2", desc: "Ana Hedef (2.5x ATR) - %50 pozisyon", color: "bg-emerald-500/20 text-emerald-400" },
                  { label: "TP3", desc: "Maksimum (4x ATR) - %20 pozisyon", color: "bg-cyan-500/20 text-cyan-400" },
                  { label: "SL", desc: "Zarar Kes (1x ATR) - Sermaye koruması", color: "bg-red-500/20 text-red-400" }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4 items-center">
                    <div className={`w-10 h-10 rounded-lg ${item.color} flex items-center justify-center shrink-0`}>
                      <span className="font-bold text-sm">{item.label}</span>
                    </div>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bildirimler */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Telegram & Uygulama Bildirimleri</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Smartphone className="h-8 w-8" />, title: "Web Dashboard", desc: "Tüm sinyalleri ve performansı görüntüle", badge: "Ücretsiz", badgeColor: "bg-slate-500/20 text-slate-400" },
              { icon: <MessageCircle className="h-8 w-8" />, title: "Telegram", desc: "Anlık sinyal uyarıları ve raporlar", badge: "Pro", badgeColor: "bg-blue-500/20 text-blue-400" },
              { icon: <Bell className="h-8 w-8" />, title: "Push Bildirim", desc: "Masaüstü anlık uyarılar", badge: "Pro", badgeColor: "bg-amber-500/20 text-amber-400" },
              { icon: <Mail className="h-8 w-8" />, title: "E-posta", desc: "Günlük performans özeti", badge: "Pro", badgeColor: "bg-purple-500/20 text-purple-400" }
            ].map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }} viewport={{ once: true }}>
                <Card className="border-slate-700/50 bg-slate-900/50 h-full text-center">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center mx-auto mb-4 text-white">{item.icon}</div>
                    <Badge className={`mb-3 ${item.badgeColor}`}>{item.badge}</Badge>
                    <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Kimler İçin */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 px-3 py-1 bg-purple-500/10 text-purple-400 border-purple-500/20">
              <Users className="h-3 w-3 mr-1" /> Hedef Kitle
            </Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Borsamex Kimler İçin Tasarlandı?</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              { icon: <Target className="h-7 w-7" />, title: "Disiplinli Traderlar", desc: "İşlem planına sadık kalan, risk yönetimini ciddiye alan kullanıcılar.", color: "bg-brand-green/20 text-brand-green" },
              { icon: <BarChart3 className="h-7 w-7" />, title: "Manuel İşlemciler", desc: "Kendi borsasında işlem yapan, sinyal ve analizleri karar destek aracı olarak kullananlar.", color: "bg-blue-500/20 text-blue-400" },
              { icon: <Activity className="h-7 w-7" />, title: "Teknik Analiz Takipçileri", desc: "Algoritmik yapı ve teknik seviye takibi yapmak isteyen kullanıcılar.", color: "bg-purple-500/20 text-purple-400" }
            ].map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }} viewport={{ once: true }}>
                <Card className="border-slate-700/50 bg-slate-900/50 h-full">
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-2xl ${item.color} flex items-center justify-center mb-4`}>{item.icon}</div>
                    <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-green-500/20 bg-green-950/10">
              <CardContent className="p-5">
                <h4 className="font-semibold text-green-400 mb-3 flex items-center gap-2"><CheckCircle className="h-5 w-5" />Uygun Olanlar</h4>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Risk yönetimini bilen ve uygulayan</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Kaybetmeyi göze alabileceği miktarla işlem yapan</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Sabırlı ve plana sadık kalabilen</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-red-500/20 bg-red-950/10">
              <CardContent className="p-5">
                <h4 className="font-semibold text-red-400 mb-3 flex items-center gap-2"><AlertTriangle className="h-5 w-5" />Uygun Olmayanlar</h4>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start gap-2"><span className="text-red-500 mt-0.5 shrink-0">✗</span>Garantili kazanç beklentisi olanlar</li>
                  <li className="flex items-start gap-2"><span className="text-red-500 mt-0.5 shrink-0">✗</span>Kaybetmeyi kaldıramayacak olanlar</li>
                  <li className="flex items-start gap-2"><span className="text-red-500 mt-0.5 shrink-0">✗</span>Stop loss kullanmayanlar</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Güvenlik */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 px-3 py-1 bg-green-500/10 text-green-400 border-green-500/20"><Shield className="h-3 w-3 mr-1" /> Güvenlik</Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Platform Güvenliği</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: <Lock className="h-6 w-6" />, title: "API Bağlantısı Yok", desc: "Borsanıza bağlanmıyoruz" },
              { icon: <DollarSign className="h-6 w-6" />, title: "Fon Erişimi Yok", desc: "Paranız kendi hesabınızda" },
              { icon: <Shield className="h-6 w-6" />, title: "Otomatik İşlem Yok", desc: "Tüm kararlar size ait" },
              { icon: <Eye className="h-6 w-6" />, title: "Şeffaf Performans", desc: "Tüm geçmiş veriler açık" }
            ].map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }} viewport={{ once: true }}>
                <Card className="border-green-500/20 bg-green-950/10 h-full">
                  <CardContent className="p-5">
                    <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center mb-3 text-green-400">{item.icon}</div>
                    <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                    <p className="text-sm text-slate-400">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Sıkça Sorulan Sorular</h2>
            <p className="text-slate-400">Yapay zeka destekli kripto sinyalleri hakkında merak edilenler</p>
          </div>

          <div className="space-y-4">
            {faqData.map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }} viewport={{ once: true }}>
                <details className="group">
                  <summary className="flex items-center justify-between p-4 bg-slate-800/50 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-800 transition-colors">
                    <span className="font-medium text-white pr-4">{item.question}</span>
                    <ChevronRight className="h-5 w-5 text-slate-400 group-open:rotate-90 transition-transform shrink-0" />
                  </summary>
                  <div className="px-4 py-3 text-sm text-slate-400 leading-relaxed">{item.answer}</div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-t from-brand-green/10 to-transparent">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Algoritmik Sinyal Sistemini Dene</h2>
            <p className="text-lg text-slate-400 mb-8">Ücretsiz kayıt ol, sinyalleri takip et, kendi kararını ver.</p>
            
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-10 py-6 text-lg rounded-xl shadow-lg shadow-brand-green/25">
                Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            
            <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-slate-500">
              <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-brand-green" /><span>Kredi kartı gerekmez</span></div>
              <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-brand-green" /><span>30 saniyede kayıt</span></div>
              <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-brand-green" /><span>İstediğin zaman iptal</span></div>
            </div>

            <p className="mt-12 text-xs text-slate-600 max-w-xl mx-auto">
              Borsamex sistem çıktıları yatırım tavsiyesi niteliği taşımaz. Kripto para ticareti yüksek risk içerir. Geçmiş performans gelecek sonuçları garanti etmez.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
